import React, { ReactElement, RefObject, useContext, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WebMediaFolder } from '../../graphql/entities';
import mapStateToFormProps from '../../utils/mapStateToFormProps';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import { useHistory, useLocation, useParams } from 'react-router';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import { useMutation } from '@apollo/client';
import { IWebMediaFile, IWebMediaFolder } from '../../graphql/schema/p4types';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { EntityType } from '../../@types/entityTypes';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import WebMediaFolderListQuery from '../../graphql/queries/web/WebMediaFolderListQuery.graphql';
import WebMediaFileMutation from '../../graphql/mutations/web/WebMediaFileMutation.graphql';
import useEntityCacheUpdate from '../../graphql/hooks/useEntityCacheUpdate';
import styled from 'styled-components';
import { appActions } from '../../state/modules/app';
import Icon from '@mdi/react';
import { mdiCloudUploadOutline } from '@mdi/js';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import getClientMutationId from '../../utils/getClientMutationId';
import BreadCrumbs from '../../components/connectedForm/BreadCrumbs';
import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import HeaderTitle from '../../components/connectedForm/HeaderTitle';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import { Button, PrimaryButton, DangerButton } from '../../components/connectedForm/Button';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import FluentModal from '../../components/modals/FluentModal';

const { default: defaultQuery, formTitle, instance, update, fragment, type, returnUrl } = WebMediaFolder;

const HeaderBlock = ({
  title,
  handleClick,
  handleDeleteClick,
  allowInsert,
  handleInsertClick,
  selectedFile,
}: {
  title: string;
  handleClick: any;
  handleDeleteClick: any;
  allowInsert: boolean;
  handleInsertClick: any;
  selectedFile: any;
}) => {
  const history = useHistory();
  const location = useLocation<any>();

  const cancel = () => {
    const previousUrl = location?.state?.previousLocation;
    if (history && previousUrl) {
      history.goBack();
    }
  };

  return (
    <>
      {!allowInsert && <BreadCrumbs title={'Web Media'} />}
      <HeaderContainer>
        <HeaderTitle title="WEB" value={title} optValue={null} />
        <HeaderAction>
          <Button onClick={cancel}>Cancel</Button>
          {selectedFile !== null ? <DangerButton onClick={handleDeleteClick}>Delete File</DangerButton> : null}
          <PrimaryButton onClick={handleClick}>Add File</PrimaryButton>
          {allowInsert && selectedFile !== null ? (
            <PrimaryButton onClick={handleInsertClick}>Insert</PrimaryButton>
          ) : null}
        </HeaderAction>
      </HeaderContainer>
    </>
  );
};

const MediaFileContainer = styled.div`
  max-height: 33%;
  width: 18%;
  display: flex;
  flex-flow: row wrap;
  align-content: space-between;
  justify-content: space-between;
  margin-left: 5px;
  margin-right: 5px;
  margin-bottom: 10px;
  border: 1px solid #eee;
  background-color: #fff;
  cursor: pointer;
  padding: 10px;
  //box-shadow: 5px 5px 10px #ddd;
`;

const ImageContainer = styled.div`
  height: 125px;
  margin: 0 auto 10px;
`;

const MediaFileImage = styled.img<any>`
  max-width: 100%;
  height: 100%;
  object-fit: contain;
  margin: 0 auto;
  display: block;
`;

const MediaFileName = styled.div`
  width: 100%;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  margin-bottom: 5px;
  overflow: hidden;
  max-height: 60px;
`;

const MediaFileSize = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.grey120};
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'Kb', 'Mb', 'Gb'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const MediaFileEntry = ({
  webMediaFolder,
  mediaFile,
  activeFile,
  onClick,
}: {
  webMediaFolder: IWebMediaFolder;
  mediaFile: IWebMediaFile;
  activeFile: IWebMediaFile | null;
  onClick: (entry: any) => void;
}) => {
  const fileExtension = mediaFile.fileName!.split('.').pop(); // Get the file extension

  let src;
  if (fileExtension && ['jpg', 'jpeg', 'gif', 'png'].includes(fileExtension.toLowerCase())) {
    src = `/api/resources/web/${webMediaFolder.breadcrumb!.join('/')}/${mediaFile.fileName}`;
  } else {
    src = '/api/resources/image/default-no-image.png';
  }
  return (
    <MediaFileContainer
      style={{
        border: activeFile?.id === mediaFile.id ? '2px solid #534bae' : '1px solid #eee',
      }}
      key={`product_${mediaFile.id}`}
      onClick={() => onClick(mediaFile)}
    >
      <ImageContainer>
        <MediaFileImage draggable={false} src={src} alt={mediaFile.fileName} />
      </ImageContainer>
      <div style={{ width: '100%', whiteSpace: 'nowrap' }}>
        <TooltipHost content={mediaFile.fileName!.toString()} id="testtooltip">
          <MediaFileName>{mediaFile.fileName}</MediaFileName>
        </TooltipHost>
      </div>
      <MediaFileSize>{formatBytes(mediaFile.fileSize)}</MediaFileSize>
    </MediaFileContainer>
  );
};

export const MediaFileList = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  height: 100%;
  width: 100%;
  padding-top: 10px;
  overflow-x: hidden;
`;

const WebMediaFolderForm = (props: {
  values: IWebMediaFolder;
  allowInsert: boolean;
  onInsertClick: (value: string) => void;
}) => {
  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);
  const [activeFile, setActiveFile] = useState(null);
  const fileSelectorRef = useRef<HTMLInputElement>();
  const handleQueryError = useComponentQueryErrorHandling();
  const [mediaFileModalOpen, setMediaFileModalOpen] = useState(false);

  const webMediaFolder = props.values;

  const [webMediaFileMutation] = useMutation(WebMediaFileMutation, {
    onError: handleQueryError,
  });

  const children: Array<ReactElement> = [];

  useMemo(() => {
    setActiveFile(null);
  }, []);

  const stopPropagation = (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleClick = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const clickEvent = new MouseEvent('click', {
      view: window,
      bubbles: false,
      cancelable: false,
    });
    fileSelectorRef.current?.dispatchEvent?.(clickEvent);
  };

  const handleDeleteClick = () => {
    setMediaFileModalOpen(true);
  };

  const handleInsertClick = () => {
    if (props.onInsertClick && activeFile) {
      props.onInsertClick(`${webMediaFolder.breadcrumb!.join('/')}/${activeFile.fileName}`);
    }
  };

  const handleDeleteFile = () => {
    webMediaFileMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          webMediaFile: {
            id: activeFile?.id,
            deleted: true,
          },
        },
      },
    }).then(() => {
      setMediaFileModalOpen(false);
      setActiveFile(null);
      connectedForm.reloadData!(true);
    });
  };

  const handleDrop = (event: { preventDefault: () => void; dataTransfer: { files: any[] } }) => {
    event.preventDefault();
    handleFileUpload(event.dataTransfer.files[0]);
  };

  const handleFileSelect = (event: { preventDefault: () => void; target: HTMLInputElement }) => {
    event.preventDefault();
    handleFileUpload(event.target.files![0]);
  };

  const handleFileUpload = (file: File) => {
    const formData = new FormData();
    const newFilename = `${webMediaFolder.breadcrumb!.join('/')}/${file.name}`;
    const amendedFile = new File([file], newFilename, { type: file.type });
    formData.append('file', amendedFile);

    const onSuccess = (response: { response: { successful: any; filename: any } }) => {
      if (response.response.successful) {
        webMediaFileMutation({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              webMediaFile: {
                fileName: response.response.filename,
                webMediaFolder: {
                  id: webMediaFolder.id,
                  uuid: webMediaFolder.uuid,
                  concurrencyVersion: webMediaFolder.concurrencyVersion,
                  files: [],
                },
              },
            },
          },
        }).then(() => {
          connectedForm.reloadData!(true);
        });
      }
    };

    dispatch(appActions.uploadImage(formData, 'resources/web/upload')).then(onSuccess);
  };

  const onClick = (mediaFile: any) => {
    setActiveFile(mediaFile);
  };

  if (!webMediaFolder.id) {
    return (
      <div key="no-images-upload-link" style={{ display: 'block', margin: '20px auto', textAlign: 'center' }}>
        <p>Please add a folder to start uploading files</p>
      </div>
    );
  }

  if (webMediaFolder.files && webMediaFolder.files.length > 0) {
    webMediaFolder.files.forEach((mediaFile) => {
      children.push(
        <MediaFileEntry
          key={`media_file_entry_${mediaFile!.id}`}
          webMediaFolder={webMediaFolder}
          mediaFile={mediaFile as IWebMediaFile}
          activeFile={activeFile}
          onClick={onClick}
        />
      );
    });
  } else {
    children.push(
      <div key="no-images-upload-link" style={{ display: 'block', margin: '20px auto', textAlign: 'center' }}>
        <p>Drop files here to upload, or click &lsquo;Add File&rsquo; above</p>
        <Icon path={mdiCloudUploadOutline} spin={false} size={3} />
      </div>
    );
  }

  return (
    <Form.FormContainer>
      <HeaderBlock
        title={webMediaFolder.name as string}
        handleClick={handleClick}
        handleDeleteClick={handleDeleteClick}
        allowInsert={props.allowInsert}
        handleInsertClick={handleInsertClick}
        selectedFile={activeFile}
      />
      <Form.Form style={{ backgroundColor: '#faf9f8' }}>
        <Form.Row>
          <input
            name="file"
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="image/png,image/gif,image/jpeg,application/pdf"
            ref={fileSelectorRef as RefObject<HTMLInputElement>}
            multiple={true}
          />
          <MediaFileList
            onDragEnter={(e) => stopPropagation(e)}
            onDragOver={(e) => stopPropagation(e)}
            onDragExit={(e) => stopPropagation(e)}
            onDrop={handleDrop}
          >
            {children}
          </MediaFileList>
        </Form.Row>
      </Form.Form>
      <FileDeleteConfirmationModal
        isOpen={mediaFileModalOpen}
        onClick={handleDeleteFile}
        onClose={() => {
          setMediaFileModalOpen(false);
        }}
      />
    </Form.FormContainer>
  );
};

export interface FileDeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClick: () => void;
}
export const FileDeleteConfirmationModal = ({ isOpen, onClose, onClick }: FileDeleteConfirmationModalProps) => {
  return (
    <FluentModal isOpen={isOpen}>
      <BasicHeader>{'Confirmation Required'}</BasicHeader>
      <div style={{ padding: '12px 20px' }}>Do you want to delete the selected file?</div>
      <ModalBottomButtonContainer>
        <Button data-testid="globalAmendModal-cancel" onClick={onClose}>
          No
        </Button>
        <PrimaryButton onClick={onClick} data-testid="globalAmendModal-amend">
          Yes
        </PrimaryButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

const ConnectedWebMediaFolderForm = createForm({
  form: type,
  name: formTitle,
  entity: WebMediaFolder,
  queryFragment: fragment,
  defaultReturnUrl: returnUrl,
  forceUpdate: true,
})(WebMediaFolderForm);

export default ({ overrideParams, ...props }: any) => {
  const formProps = useSelector((store) => mapStateToFormProps(store, props));
  const formDefault = useFormQuery({ default: defaultQuery, instance: instance } as EntityType, {
    duplicate: true,
    overrideParams,
  });

  const cacheUpdate = useEntityCacheUpdate(undefined, 'webMediaFolder', { detailView: true });
  const [formUpdate] = useMutation(update, {
    onCompleted: (data) => {
      props.setSelectedNode(data.webMediaFolderUpdate.result.id);
    },
    update: cacheUpdate,
    refetchQueries: [WebMediaFolderListQuery],
  });

  const params = useParams<{ id?: string }>();

  const id = overrideParams?.id || params.id;
  const isRoot = overrideParams?.isRoot;
  const parentId = overrideParams?.parentId;
  const newWebMediaFolder = !(id && id !== 'New');
  const webMediaFolderId = newWebMediaFolder ? null : id;

  let formTitle = WebMediaFolder.formTitle;
  if (!(id && id !== 'New')) {
    formTitle = isRoot ? 'New Root Category' : 'New Sub Category';
  }

  return (
    <ConnectedWebMediaFolderForm
      {...formProps}
      {...props}
      formUpdate={formUpdate}
      formData={formDefault}
      name={formTitle}
      newWebMediaFolder={newWebMediaFolder}
      webMediaFolderId={webMediaFolderId}
      isRoot={isRoot}
      parentId={parentId}
    />
  );
};
